/**
 * Port di ZionPrivacyCrypto.kt — envelope JSON per messaggi strutturati.
 */
import {
  decryptForContact,
  encryptForContact,
  looksEncrypted,
  stripE2EPrefix,
} from './ZionCipher.ts'
import type { ReplyTarget } from '../chat/messageReplyControl.ts'
import { parseReplyFromEnvelope, wrapReplyEnvelope, type AndroidTextEnvelope } from '../chat/messageReplyControl.ts'
import { buildEphemeralTimerFields } from '../tempChat/ephemeralPayload.ts'
import { isMediaKind, normalizeKind } from '../media/zionMediaProtocol.ts'

export interface MediaPayload {
  kind: string
  mime: string
  mediaB64: string
  caption?: string
  fileName?: string
  mediaGzip?: boolean
  mediaOrigSize?: number
  /** Durata vocale in millisecondi (Android UI / ExoPlayer fallback). */
  durationMs?: number
}

export interface AndroidMediaEnvelope extends AndroidTextEnvelope {
  contentKind?: string
  mime?: string
  mediaB64?: string
  fileName?: string
  mediaGzip?: boolean
  mediaOrigSize?: number
  /** Durata audio ms — letto da Android VoiceBubble. */
  durationMs?: number
}

export interface ZionTextEnvelope {
  v: 1
  type: 'text'
  body: string
  ts?: number
}

export interface ZionJsonEnvelope {
  v: 1
  type: string
  payload: unknown
  ts?: number
}

export interface ParsedPlainMessage {
  text: string
  senderId?: string
  messageId?: string
  /** Timestamp envelope Android (ms), se presente. */
  timestamp?: number
  replyTo?: ReplyTarget
  contentKind?: string
  mediaB64?: string
  mime?: string
  fileName?: string
  mediaGzip?: boolean
  mediaOrigSize?: number
  durationMs?: number
}

export function wrapAndroidTextEnvelope(
  plainText: string,
  senderId: string,
  messageId: string,
  ts = Date.now(),
  reply?: ReplyTarget,
  /**
   * @deprecated Non usare per invii normali verso Android.
   * Il TTL va solo nel testo (`NYXIA_TTL_VIEW::`) per retrocompatibilità.
   * I campi isEphemeral/ttl/durationMs sull'envelope rompono parser Android vecchi.
   */
  ephemeralTtlMs?: number | null,
): AndroidTextEnvelope {
  // Envelope classico = stesso schema di ZionPrivacyCrypto.encryptForContact (Android).
  // Niente campi Auto-Healing / ephemeral extra: il destinatario legacy legge `text` e basta.
  const base: AndroidTextEnvelope = reply
    ? wrapReplyEnvelope(plainText, senderId, messageId, reply, ts)
    : {
        v: 1,
        senderId: senderId.trim(),
        text: plainText,
        timestamp: ts,
        messageId,
      }
  // Solo se esplicitamente richiesto (legacy API) — preferire wrapWithViewTtl sul testo
  if (ephemeralTtlMs != null && ephemeralTtlMs > 0) {
    return { ...base, ...buildEphemeralTimerFields(ephemeralTtlMs) }
  }
  return base
}

/**
 * Envelope testo minimo allineato ad Android classico (v/senderId/text/timestamp/messageId).
 * Usare sempre questo per messaggi utente verso peer misti PWA/Android.
 */
export function wrapClassicAndroidTextEnvelope(
  plainText: string,
  senderId: string,
  messageId: string,
  ts = Date.now(),
  reply?: ReplyTarget,
): AndroidTextEnvelope {
  return wrapAndroidTextEnvelope(plainText, senderId, messageId, ts, reply, null)
}

export function wrapTextEnvelope(plainText: string, ts = Date.now()): ZionTextEnvelope {
  return { v: 1, type: 'text', body: plainText, ts }
}

export function serializeEnvelope(envelope: ZionTextEnvelope | ZionJsonEnvelope): string {
  return JSON.stringify(envelope)
}

export function parseEnvelope(raw: string): ZionTextEnvelope | ZionJsonEnvelope | null {
  const trimmed = raw.trim()
  if (!trimmed.startsWith('{')) return null
  try {
    const parsed = JSON.parse(trimmed) as ZionTextEnvelope | ZionJsonEnvelope
    if (parsed && typeof parsed === 'object' && parsed.v === 1 && typeof parsed.type === 'string') {
      return parsed
    }
  } catch {
    return null
  }
  return null
}

export async function encryptMediaMessage(
  myPrivateKey: CryptoKey,
  caption: string,
  contactPublicKeyBase64: string,
  media: MediaPayload,
  senderId = '',
  messageId = String(Date.now()),
): Promise<string> {
  const contentKind = normalizeKind(media.kind)
  const envelope: AndroidMediaEnvelope = {
    v: 1,
    senderId: senderId.trim(),
    text: caption,
    timestamp: Date.now(),
    messageId,
    contentKind,
    mime: media.mime,
    mediaB64: media.mediaB64,
  }
  if (media.fileName?.trim()) envelope.fileName = media.fileName.trim()
  if (media.mediaGzip) envelope.mediaGzip = true
  if (media.mediaOrigSize != null && media.mediaOrigSize > 0) {
    envelope.mediaOrigSize = media.mediaOrigSize
  }
  // Vocali: durationMs sempre presente nell'envelope cifrato (Android UI)
  const durationMs = media.durationMs != null ? Math.round(media.durationMs) : 0
  if (contentKind === 'audio') {
    if (durationMs <= 0) {
      throw new Error('durationMs richiesto per messaggi audio')
    }
    envelope.durationMs = durationMs
  } else if (durationMs > 0) {
    envelope.durationMs = durationMs
  }
  return encryptForContact(myPrivateKey, JSON.stringify(envelope), contactPublicKeyBase64)
}

export async function encryptTextMessage(
  myPrivateKey: CryptoKey,
  plainText: string,
  contactPublicKeyBase64: string,
  senderId = '',
  messageId = String(Date.now()),
  reply?: ReplyTarget,
  /**
   * Ignorato per default compat: TTL deve stare già in `plainText` via wrapWithViewTtl.
   * Non si aggiungono campi ephemeral JSON (romperebbero Android non aggiornato).
   */
  _ephemeralTtlMs?: number | null,
): Promise<string> {
  const envelope = JSON.stringify(
    wrapClassicAndroidTextEnvelope(plainText, senderId, messageId, Date.now(), reply),
  )
  return encryptForContact(myPrivateKey, envelope, contactPublicKeyBase64)
}

export async function encryptControlMessage(
  myPrivateKey: CryptoKey,
  controlPlain: string,
  contactPublicKeyBase64: string,
  senderId: string,
  messageId: string,
  _ephemeralTtlMs?: number | null,
): Promise<string> {
  // Control in envelope classico (text = JSON ACK/resync) — fire-and-forget, non altera il messaggio utente
  const envelope = JSON.stringify(
    wrapClassicAndroidTextEnvelope(controlPlain, senderId, messageId, Date.now()),
  )
  return encryptForContact(myPrivateKey, envelope, contactPublicKeyBase64)
}

export async function decryptTextMessage(
  myPrivateKey: CryptoKey,
  payload: string,
  contactPublicKeyBase64: string,
): Promise<string> {
  const decrypted = await decryptForContact(myPrivateKey, payload, contactPublicKeyBase64)
  const parsed = extractPlainText(decrypted)
  return parsed?.text ?? decrypted
}

/**
 * Parser inbound tollerante (Postel's Law / retrocompatibilità).
 * Accetta envelope Android v1, legacy `{type:text,body}`, JSON senza `v`, testo plain.
 * Non scarta se mancano campi ACK/resync/metadati nuovi.
 */
export function extractPlainText(decrypted: string): ParsedPlainMessage | null {
  const trimmed = String(decrypted || '').trim().replace(/^\uFEFF/, '')
  if (!trimmed) return null

  const envelope = parseEnvelope(trimmed)
  if (envelope?.type === 'text' && 'body' in envelope && typeof envelope.body === 'string') {
    return { text: envelope.body }
  }
  try {
    const android = JSON.parse(trimmed) as AndroidMediaEnvelope & {
      body?: string
      ts?: number
      message_id?: string | number
    }
    // Tollerante: v assente o diverso da 1 → prova comunque se c'è text/body/media
    const contentKind = normalizeKind(android.contentKind ?? 'text')
    const mediaB64 = typeof android.mediaB64 === 'string' ? android.mediaB64.trim() : ''
    const mime = typeof android.mime === 'string' ? android.mime.trim() : ''
    const fileName = typeof android.fileName === 'string' ? android.fileName.trim() : undefined
    const mediaGzip = android.mediaGzip === true
    const mediaOrigSize =
      typeof android.mediaOrigSize === 'number' && android.mediaOrigSize > 0
        ? android.mediaOrigSize
        : undefined
    const durationMs =
      typeof android.durationMs === 'number' && android.durationMs > 0
        ? Math.round(android.durationMs)
        : undefined
    const text =
      typeof android.text === 'string' && android.text
        ? android.text
        : typeof android.body === 'string'
          ? android.body
          : ''
    const hasMedia = isMediaKind(contentKind) && mediaB64.length > 0
    if (text || hasMedia) {
      const rawTs = android.timestamp ?? android.ts
      const envelopeTs =
        typeof rawTs === 'number' && Number.isFinite(rawTs) && rawTs > 0
          ? Math.trunc(rawTs)
          : undefined
      const mid =
        typeof android.messageId === 'string'
          ? android.messageId
          : android.message_id != null
            ? String(android.message_id)
            : String(envelopeTs ?? '')
      return {
        text,
        senderId: typeof android.senderId === 'string' ? android.senderId.trim() : undefined,
        messageId: mid,
        timestamp: envelopeTs,
        replyTo: parseReplyFromEnvelope(android) ?? undefined,
        contentKind: hasMedia ? contentKind : undefined,
        mediaB64: hasMedia ? mediaB64 : undefined,
        mime: hasMedia ? mime : undefined,
        fileName: hasMedia ? fileName : undefined,
        mediaGzip: hasMedia && mediaGzip ? true : undefined,
        mediaOrigSize: hasMedia ? mediaOrigSize : undefined,
        durationMs: hasMedia ? durationMs : undefined,
      }
    }
  } catch {
    /* not JSON */
  }
  // Legacy: ciphertext decifrato = testo utente grezzo
  return { text: trimmed }
}

/** Come Android — blob sulla propria coda. */
export async function tryDecryptFromAllContacts(
  myPrivateKey: CryptoKey,
  blob: string,
  contacts: { publicKey: string }[],
): Promise<{
  index: number
  text: string
  senderId?: string
  messageId?: string
  timestamp?: number
  replyTo?: ReplyTarget
  contentKind?: string
  mediaB64?: string
  mime?: string
  fileName?: string
  mediaGzip?: boolean
  mediaOrigSize?: number
  durationMs?: number
} | null> {
  for (let i = 0; i < contacts.length; i++) {
    const contact = contacts[i]!
    try {
      const decrypted = await decryptForContact(myPrivateKey, blob, contact.publicKey)
      const parsed = extractPlainText(decrypted)
      if (parsed != null) {
        return {
          index: i,
          text: parsed.text,
          senderId: parsed.senderId,
          messageId: parsed.messageId,
          timestamp: parsed.timestamp,
          replyTo: parsed.replyTo,
          contentKind: parsed.contentKind,
          mediaB64: parsed.mediaB64,
          mime: parsed.mime,
          fileName: parsed.fileName,
          mediaGzip: parsed.mediaGzip,
          mediaOrigSize: parsed.mediaOrigSize,
          durationMs: parsed.durationMs,
        }
      }
    } catch {
      /* wrong contact */
    }
  }
  return null
}

export function extractCipherBody(payload: string): string {
  return stripE2EPrefix(payload) ?? payload
}

export function isEncryptedPayload(text: string): boolean {
  return looksEncrypted(text)
}
